/**
 * Admin Tasks Operations Handler
 */

import { supabase } from '../../supabase';
import { ParsedCommand, ExecutionResult } from '../types';

export async function executeAdminTaskOperation(
  command: ParsedCommand,
  userId: string
): Promise<ExecutionResult> {
  const { intent, entities } = command;

  try {
    switch (intent) {
      case 'query_admin_tasks':
        return await queryAdminTasks(entities.filters);

      case 'update_admin_task':
        if (!entities.target_id) {
          return {
            success: false,
            error: 'No admin task ID provided',
            message: 'Please specify which admin task to update',
          };
        }
        return await updateAdminTask(entities.target_id, entities.updates, userId);

      case 'create_admin_task':
        return await createAdminTask(entities.new_record, userId);

      default:
        return {
          success: false,
          error: `Unsupported admin task operation: ${intent}`,
          message: 'Operation not supported',
        };
    }
  } catch (error) {
    console.error('Error in admin task operation:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      message: 'Failed to execute admin task operation',
    };
  }
}

async function queryAdminTasks(filters?: Record<string, any>): Promise<ExecutionResult> {
  let query = supabase
    .from('admin_tasks')
    .select(`
      id,
      title,
      description,
      status,
      priority,
      due_date,
      created_at,
      completed_at,
      assigned_to,
      home:homes(
        id,
        address,
        city,
        state
      ),
      homeowner:profiles!admin_tasks_homeowner_id_fkey(
        id,
        full_name
      ),
      service_request:service_requests(
        id,
        title
      )
    `)
    .order('created_at', { ascending: false });

  // Apply filters
  if (filters) {
    if (filters.status) {
      if (Array.isArray(filters.status)) {
        query = query.in('status', filters.status);
      } else {
        query = query.eq('status', filters.status);
      }
    }

    if (filters.priority) {
      if (Array.isArray(filters.priority)) {
        query = query.in('priority', filters.priority);
      } else {
        query = query.eq('priority', filters.priority);
      }
    }

    if (filters.assigned_to) {
      query = query.eq('assigned_to', filters.assigned_to);
    }

    if (filters.home_id) {
      query = query.eq('home_id', filters.home_id);
    }
  }

  const { data, error } = await query.limit(50);

  if (error) {
    throw error;
  }

  return {
    success: true,
    data,
    message: `Found ${data?.length || 0} admin task${data?.length !== 1 ? 's' : ''}`,
    affectedRecords: data?.length || 0,
    recordDetails: data,
  };
}

async function updateAdminTask(
  id: string,
  updates?: Record<string, any>,
  userId?: string
): Promise<ExecutionResult> {
  if (!updates || Object.keys(updates).length === 0) {
    return {
      success: false,
      error: 'No updates provided',
      message: 'Please specify what to update',
    };
  }

  // Fetch current record
  const { data: current, error: fetchError } = await supabase
    .from('admin_tasks')
    .select('id, title, status')
    .eq('id', id)
    .single();

  if (fetchError || !current) {
    return {
      success: false,
      error: 'Admin task not found',
      message: `Admin task ${id} not found`,
    };
  }

  // If marking as completed, set completed_at
  if (updates.status === 'completed' && !updates.completed_at) {
    updates.completed_at = new Date().toISOString();
  }

  // Perform update
  const { data, error } = await supabase
    .from('admin_tasks')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return {
    success: true,
    data,
    message: `Successfully updated admin task "${current.title}"`,
    affectedRecords: 1,
    recordDetails: [data],
  };
}

async function createAdminTask(
  newRecord?: Record<string, any>,
  userId?: string
): Promise<ExecutionResult> {
  if (!newRecord) {
    return {
      success: false,
      error: 'No data provided',
      message: 'Please specify the admin task details',
    };
  }

  // Ensure required fields
  if (!newRecord.title) {
    return {
      success: false,
      error: 'Missing required field: title',
      message: 'Please provide a title for the admin task',
    };
  }

  // Set defaults
  const recordData = {
    title: newRecord.title,
    description: newRecord.description || null,
    status: newRecord.status || 'pending',
    priority: newRecord.priority || 'normal',
    home_id: newRecord.home_id || null,
    homeowner_id: newRecord.homeowner_id || null,
    service_request_id: newRecord.service_request_id || null,
    created_by: userId,
    assigned_to: newRecord.assigned_to || userId,
    due_date: newRecord.due_date || null,
  };

  const { data, error } = await supabase
    .from('admin_tasks')
    .insert(recordData)
    .select()
    .single();

  if (error) {
    throw error;
  }

  return {
    success: true,
    data,
    message: `Successfully created admin task "${data.title}"`,
    affectedRecords: 1,
    recordDetails: [data],
  };
}
